/*
 * The flashcard scheduler, ported from the Facecards app's Learn engine
 * (~/hacks/app-script-facecards js-learn.html / js-review.html; the knobs
 * and the properties worth preserving are documented in its TUNING.md).
 *
 * Probabilistic spaced repetition via a per-card forgetting curve. Each
 * card carries a stability s (a half-life measured in cards asked, not
 * wall-clock time) and the trial number at which it was last shown. The
 * predicted chance the student still recalls a card t trials after seeing
 * it is R = 2^(-t/s), so its urgency — the chance it has been forgotten —
 * is w = 1 - R. The next card is drawn at random with probability
 * proportional to w.
 *
 * The bookkeeping falls out of the decay function: showing a card resets t
 * to 0, so its urgency drops to nothing and climbs back on its own. A
 * correct answer multiplies s by 1 + GROWTH * (1 - R): the more forgotten
 * the card was when it was asked, the more the correct answer proves, so
 * the more its stability grows. (Answering while the card is still fresh
 * proves little, so a card can only graduate by surviving real gaps.) A
 * miss resets s to the starting value, so the card reasserts itself soon.
 * New cards are dealt in only when no already-seen card is urgent enough —
 * which also means that when every active card is still fresh, the pool
 * grows instead of re-asking too early, and gaps stretch on their own. A
 * card graduates out of the session once s reaches the graduation bar
 * (GRADUATED_S, capped only for tiny decks that can't produce gaps that
 * long). Once the deck is exhausted, the same not-urgent-enough gate deals
 * already-graduated cards back in as spacers, so the last stragglers still
 * get real gaps instead of being drilled back-to-back — and missing a
 * spacer un-graduates it. The session ends when every card has graduated.
 *
 * Review mode is the same engine with an optimistic prior: unseen cards
 * enter play with stability already at the graduation bar, so a correct
 * first answer graduates the card on the spot (a perfect run is a single
 * shuffled pass) and a miss drops it into the Learn schedule. The prior
 * also flips where spacers come from (spacersFromDeck): right after a
 * miss, Review deals an unseen card (presumed known) rather than re-showing
 * one just confirmed; Learn keeps the opposite order so a miss never pulls
 * new material into the pool.
 *
 * Pure: no DOM, and the randomness is injectable (tests drive it with a
 * seeded generator). Cards are any objects, compared by identity.
 */

export const LEARN_START_S = 1; // stability of a new or just-missed card
export const LEARN_GROWTH = 3; // stability multiplier is 1 + this * (1 - R)
export const LEARN_GRADUATED_S = 16; // stability at which a card leaves the session
// Deal a new card only when nothing in play is as urgent as a just-missed
// card is one trial after the miss. Defined that way (rather than as a bare
// number) so a fresh miss always blocks new material: the very next card is
// existing material, and the missed card follows close behind.
export const LEARN_INTRODUCE_BELOW = 1 - 2 ** (-1 / LEARN_START_S);

export type Random = () => number; // [0, 1), Math.random's contract

/** A shuffled copy (Fisher–Yates). */
export const shuffled = <T>(xs: readonly T[], random: Random = Math.random): T[] => {
  const out = [...xs];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

interface Memory {
  s: number; // stability: the half-life in trials
  askedAt: number; // the trial the card was last dealt on
  askedW: number; // how forgotten it was when last dealt (1 - R)
}

export type Mode = 'learn' | 'review';

export class LearnState<T> {
  unseen: T[];
  active: T[] = []; // seen but not yet graduated
  graduated: T[] = [];
  total: number;
  remaining: number; // active + unseen
  trial = 0;
  lastCard: T | null = null;
  missed: T[] = []; // cards missed at least once, for the done-screen drills
  memory = new Map<T, Memory>();
  // Stability a card enters play with. Review overrides this with the
  // graduation bar (the optimistic prior).
  priorS: number = LEARN_START_S;
  // Where next() finds a spacer when the just-answered card is the only
  // active card: Learn re-shows a graduated card; Review deals from the deck.
  spacersFromDeck = false;
  // A deck can't produce gaps longer than the number of other cards in it.
  graduationBar: number;
  random: Random;

  constructor(cards: readonly T[], random: Random = Math.random) {
    this.random = random;
    this.unseen = shuffled(cards, random);
    this.total = cards.length;
    this.remaining = cards.length;
    this.graduationBar = Math.min(
      LEARN_GRADUATED_S,
      Math.max(2 * LEARN_START_S, 2 * (cards.length - 1)),
    );
  }

  /** A card retired in an earlier session (per saved progress) enters play
   * already graduated: it can be dealt back in as a spacer, and missing it
   * un-graduates it like any other spacer. */
  restore(card: T): void {
    const i = this.unseen.indexOf(card);
    if (i === -1) return;
    this.unseen.splice(i, 1);
    this.memory.set(card, { s: this.graduationBar, askedAt: 0, askedW: 0 });
    this.graduated.push(card);
    this.remaining--;
  }

  urgency(card: T): number {
    const m = this.memory.get(card);
    if (!m) return 1;
    return 1 - 2 ** (-(this.trial - m.askedAt) / m.s);
  }

  private deal(card: T): T {
    const m = this.memory.get(card);
    if (m) {
      m.askedW = this.urgency(card); // how forgotten it was when asked
      m.askedAt = this.trial;
    } else {
      // A brand-new card is maximally unknown; a correct first answer means
      // the student already knows it, so let it fast-track out.
      this.memory.set(card, { s: this.priorS, askedAt: this.trial, askedW: 1 });
      this.active.push(card);
    }
    this.trial++;
    this.lastCard = card;
    return card;
  }

  /** Draw from cards (excluding the card just answered) with probability
   * proportional to urgency; null if there's nothing to draw. */
  private sample(cards: readonly T[]): T | null {
    const candidates = cards.filter((c) => c !== this.lastCard);
    let total = 0;
    const weights = candidates.map((c) => {
      const w = this.urgency(c);
      total += w;
      return w;
    });
    let r = this.random() * total;
    for (let i = 0; i < candidates.length; i++) {
      r -= weights[i];
      if (r < 0) return candidates[i];
    }
    return candidates.length > 0 ? candidates[candidates.length - 1] : null;
  }

  /** The next card to show, or null when every card has graduated. */
  next(): T | null {
    if (this.remaining === 0) return null;

    // The gate counts every active card, including the one just answered: a
    // just-missed card is urgent even though it can't be re-dealt yet, so it
    // holds new material back while the sampler fills the gap with an
    // existing card.
    const maxW = this.active.reduce((max, c) => Math.max(max, this.urgency(c)), 0);

    // Nothing in play is urgent enough: bring in a new card, or once the
    // deck is empty, a graduated card as a spacer.
    if (maxW < LEARN_INTRODUCE_BELOW) {
      const fresh = this.unseen.pop();
      if (fresh !== undefined) return this.deal(fresh);
      const spacer = this.sample(this.graduated);
      if (spacer) return this.deal(spacer);
    }

    // Re-ask an active card (never the one just answered). When the card
    // just answered is the only active card, fall back to anything else in
    // play — per spacersFromDeck, a graduated spacer or a new card — before
    // re-dealing it back-to-back.
    let pick = this.sample(this.active);
    if (!pick && this.spacersFromDeck && this.unseen.length > 0) pick = this.unseen.pop() ?? null;
    if (!pick) pick = this.sample(this.graduated);
    if (!pick && this.unseen.length > 0) pick = this.unseen.pop() ?? null;
    return this.deal(pick ?? (this.lastCard as T));
  }

  correct(card: T): void {
    const m = this.memory.get(card);
    if (!m) return;
    m.s *= 1 + LEARN_GROWTH * m.askedW;
    const i = this.active.indexOf(card);
    if (i !== -1 && m.s >= this.graduationBar) {
      this.active.splice(i, 1);
      this.graduated.push(card);
      this.remaining--;
    }
  }

  incorrect(card: T): void {
    const m = this.memory.get(card);
    if (!m) return;
    m.s = LEARN_START_S;
    if (!this.missed.includes(card)) this.missed.push(card);
    // A missed spacer wasn't learned after all: un-graduate it.
    const i = this.graduated.indexOf(card);
    if (i !== -1) {
      this.graduated.splice(i, 1);
      this.active.push(card);
      this.remaining++;
    }
  }

  /** The number of cards retired so far (graduated, including restored). */
  retired(): number {
    return this.total - this.remaining;
  }
}

/** The Learn engine under Review's optimistic prior. */
export function reviewState<T>(cards: readonly T[], random: Random = Math.random): LearnState<T> {
  const state = new LearnState(cards, random);
  state.priorS = state.graduationBar;
  state.spacersFromDeck = true;
  return state;
}

export function makeState<T>(mode: Mode, cards: readonly T[], random?: Random): LearnState<T> {
  return mode === 'review' ? reviewState(cards, random) : new LearnState(cards, random);
}
